<template>
  <div
    v-if="!loading"
    :class="{ 'mt-4': hasSavings }"
  >
    <div>
      <component
        :is="hasSavings ? 'vetster-shine-border' : 'div'"
        class-name="w-full flex items-start p-5 !place-items-start relative bg-white"
        :color="['#A07CFE', '#FF48A8', '#FFBE7B']"
        :border-radius="16"
        :border-width="4"
      >
        <div
          v-if="hasSavings"
          class="absolute -top-2.5 right-5 z-50 rounded-md px-2 py-1 bg-bubblegum text-black uppercase text-xs font-semibold font-title flex items-center gap-x-1.5"
        >
          <fa
            :icon="['fas', savingsAsDollars ? 'money-bill-wave' : 'tags']"
            class="text-xs"
          />
          <span>{{ savings }}</span>
        </div>
        <div class="flex flex-row gap-x-1.5 items-center">
          <div
            v-if="!displayAsFree && hasSavings && savingsOnMonthly"
            class="line-through text-neutral-700 text-sm"
          >
            {{ formattedMonthlyPrice }}
          </div>
          <div class="flex flex-row items-center justify-between gap-x-2 w-full">
            <h4 v-if="displayAsFree">
              <span class="font-bold uppercase">{{ savings }}</span>
            </h4>
            <h4 v-else>
              <span class="font-bold">{{ formattedAdjustedMonthlyPrice }}</span>
              <span
                v-if="displayCurrency"
                v-text="` ${plan.currency}`"
              />
              <span>/{{ $t('membership-subscriptions.month') }}</span>
            </h4>
          </div>
        </div>
        <p
          class="text-xs"
          v-html="subscriptionCopy"
        />
      </component>
    </div>
    <div class="h-[calc(2.75+1.5rem)]">
      <vetster-membership-offering-button
        :button-url="ctaUrl"
        :button-class="buttonClass"
        :is-pay-as-you-go="isPayAsYouGo"
        :promo-code="promoCode"
        :button-text="buttonText"
      />
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faMoneyBillWave as fasMoneyBillWave,
  faTags as fasTags,
  faTicketAlt as fasTicketAlt,
} from '@fortawesome/pro-solid-svg-icons';
import { getCurrentInstance } from 'vue';
import { mapGetters } from 'vuex';

import VetsterMembershipOfferingButton from '@/brand-components/VetsterMembershipOfferingButton';
import VetsterShineBorder from '@/components/VetsterShineBorder';
import useMembershipPromotion from '@/composables/use-membership-promotion';
import { formatMoney } from '@/helpers/format';
import ParseErrors from '@/mixins/ParseErrors';

library.add({
  fasMoneyBillWave,
  fasTags,
  fasTicketAlt,
});

export default {
  name: 'VetsterMembershipOffering',

  components: { VetsterShineBorder, VetsterMembershipOfferingButton },

  mixins: [ParseErrors],

  props: {
    buttonClass: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: null,
    },
    ctaUrl: {
      type: String,
      default: null,
    },
    displayAsFree: {
      type: Boolean,
      default: false,
    },
    discountPercentage: {
      type: Number,
      default: 0,
    },
    displayRenewalPrice: {
      type: Boolean,
      default: false,
    },
    displaySavingsAs: {
      type: String,
      default: 'dollars',
      validator(value) {
        return ['percent', 'dollars'].includes(value);
      },
    },
    displaySavingsOn: {
      type: String,
      default: 'both',
      validator(value) {
        return ['annual', 'monthly', 'both'].includes(value);
      },
    },
    isPayAsYouGo: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      default: () => ({}),
    },
    promoCode: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const root = getCurrentInstance().proxy.$root;
    const { promotion, isPromotionEnabled } = useMembershipPromotion(root, props.plan);

    return {
      promotion,
      isPromotionEnabled,
    };
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapGetters('subscriptionPlans', ['getYearlySubscriptionPlan']),
    adjustedAnnualPrice() {
      if (this.hasDiscount || !this.hasPromotion) {
        return formatMoney(this.price.amount * (1 - this.discountPercentage));
      }

      return formatMoney(this.promotion?.annualPrice?.amount);
    },
    adjustedMonthlyPrice() {
      if (this.hasDiscount || !this.hasPromotion) {
        return formatMoney((this.price.amount * (1 - this.discountPercentage)) / 12);
      }

      return formatMoney(this.promotion.monthlyPrice.amount);
    },
    annualPrice() {
      if (this.hasDiscount || !this.hasPromotion) {
        return formatMoney(this.price.amount);
      }

      return formatMoney(this.promotion.originalAnnualPrice.amount);
    },
    currency() {
      return this.price?.currency;
    },
    displayCurrency() {
      return this.currency !== 'USD';
    },
    formattedAdjustedAnnualPrice() {
      return this.formatPrice(this.adjustedAnnualPrice);
    },
    formattedAdjustedMonthlyPrice() {
      return this.formatPrice(this.adjustedMonthlyPrice);
    },
    formattedAnnualPrice() {
      return this.formatPrice(this.annualPrice);
    },
    formattedMonthlyPrice() {
      return this.formatPrice(this.monthlyPrice);
    },
    formattedRenewalPrice() {
      return this.$t('membership-subscriptions.billed-today-and-renews-annually-at-cancel-anytime', {
        price: this.formattedAdjustedAnnualPrice,
        renewPrice: this.formattedAnnualPrice,
      });
    },
    hasDiscount() {
      return this.discountPercentage > 0;
    },
    hasPlan() {
      return Object.keys(this.plan).length > 0;
    },
    hasPromotion() {
      return !!this.promotion.isEnabled && this.discountPercentage === 0;
    },
    hasSavings() {
      return this.hasDiscount || this.hasPromotion;
    },
    monthlyPrice() {
      if (this.hasDiscount || !this.hasPromotion) {
        return this.annualPrice / 12;
      }

      return formatMoney(this.promotion.originalMonthlyPrice.amount);
    },
    price() {
      return this.getYearlySubscriptionPlan?.product?.price;
    },
    savings() {
      if (this.displayAsFree) {
        return this.$t('search.free');
      }

      return `${this.$t('app.save')} ${this.savingsAsDollars ? this.savingsDollars : this.savingsPercentage}`;
    },
    savingsAsDollars() {
      return this.displaySavingsAs === 'dollars';
    },
    savingsAsPercent() {
      return this.displaySavingsAs === 'percent';
    },
    savingsDollars() {
      const savings = Math.floor(this.annualPrice - this.adjustedAnnualPrice);
      if (savings <= 0) {
        return '';
      }

      return this.$n(Math.floor(this.annualPrice - this.adjustedAnnualPrice), {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    savingsOnAnnual() {
      return this.displaySavingsOn === 'annual' || this.savingsOnBoth;
    },
    savingsOnBoth() {
      return this.displaySavingsOn === 'both';
    },
    savingsOnMonthly() {
      return this.displaySavingsOn === 'monthly' || this.savingsOnBoth;
    },

    savingsPercentage() {
      // When :discount="0.xx" is not passed, calculate
      if (this.discountPercentage <= 0) {
        if (this.monthlyPrice > this.adjustedMonthlyPrice) {
          const calculatedDiscountPercentage = Math.abs((this.adjustedMonthlyPrice - this.monthlyPrice) / this.monthlyPrice);

          return this.$n(calculatedDiscountPercentage, {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
        }

        return '';
      }

      return this.$n(this.discountPercentage, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    subscriptionCopy() {
      if (this.adjustedAnnualPrice <= 0) {
        return this.$t('membership-subscriptions.free-for-the-first-year-renews-at-year-cancel-anytime', {
          price: `<span class="font-bold">${this.formattedAnnualPrice}</span>`,
        });
      }

      if (this.isPromotionEnabled) {
        // Price and promo price are the same - we do not need to repeat the renewal price
        if (this.formattedAdjustedAnnualPrice === this.formattedAnnualPrice) {
          return this.$t('membership-subscriptions.billed-annually-at', {
            price: this.formattedAdjustedAnnualPrice,
          });
        }

        // Price and promo price are different - clarify it renews annually
        return this.formattedRenewalPrice;
      }

      if (this.hasSavings && this.savingsOnAnnual) {
        // if (this.displayRenewalPrice || this.formattedAdjustedAnnualPrice !== this.formattedAnnualPrice) {
        if (this.displayRenewalPrice) {
          return this.formattedRenewalPrice;
        }

        return this.$t('membership-subscriptions.billed-annually-at-savings', {
          originalPrice: `<span class="line-through">${this.formattedAnnualPrice}</span> `,
          price: this.formattedAdjustedAnnualPrice,
        });
      }

      return this.$t('membership-subscriptions.billed-annually-at', {
        price: this.formattedAdjustedAnnualPrice,
        currency: this.displayCurrency ? this.plan.currency : null,
      });
    },
  },

  async created() {
    if (this.hasPlan) {
      await this.$store.commit('subscriptionPlans/setYearlySubscriptionPlan', this.plan);
    } else {
      await this.fetchSubscriptionPlan();
    }
  },

  methods: {
    async fetchSubscriptionPlan() {
      try {
        this.loading = true;
        await this.$store.dispatch('subscriptionPlans/fetchSubscriptionPlan');
      } catch (e) {
        this.showError(e, true);
      } finally {
        this.loading = false;
      }
    },

    formatPrice(amount) {
      const fractionDigits = amount % 1 === 0 ? 0 : 2;

      return this.$n(amount, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    },
  },
};
</script>
